import {
  FastField,
  FieldHookConfig,
  FieldInputProps,
  FieldMetaProps,
  useField,
} from 'formik';
import { isEmpty } from 'src/utils/typeCheck';

import { TextField, TextFieldProps } from '../../../components/TextField';
interface FormTextFieldExtraProps {
  name: string; // Use 'name' to align with useField expected field name
  /**
   * Fast is meant for performance optimization. However, you really do not need to use it until you do. Only proceed if you are familiar with how React's shouldComponentUpdate() works. You have been warned.
   * No. Seriously. Please review the following parts of the official React documentation before continuing
   * @see {@link https://legacy.reactjs.org/docs/react-component.html#shouldcomponentupdate | React shouldComponentUpdate() Reference}
   * @see {@link https://legacy.reactjs.org/docs/optimizing-performance.html#shouldcomponentupdate-in-action | React shouldComponentUpdate in Action}
   */
  fast?: boolean;
  /**
   * When false, value updates skip Formik's validateForm. Needed when a sibling
   * effect owns Location/Asset error sync after service changes.
   */
  validateOnChange?: boolean;
}

type FormTextFieldProps = TextFieldProps &
  FormTextFieldExtraProps &
  FieldHookConfig<string>;

export const FormTextField: React.FC<FormTextFieldProps> = (props) => {
  const {
    fast = false,
    onChange,
    validateOnChange = true,
    ...restProps
  } = props;

  // only enable if fast is false
  const [field, meta, helpers] = useField(props);

  const renderTextField = (
    field: FieldInputProps<string>,
    meta: FieldMetaProps<string>,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    return (
      <TextField
        error={!!(meta.touched && meta.error)}
        helperText={meta.touched && meta.error}
        {...field}
        value={isEmpty(field.value) ? '' : field.value}
        {...restProps}
        onChange={onChange}
      />
    );
  };

  if (fast) {
    return (
      <FastField name={props.name}>
        {({ field, meta }) =>
          renderTextField(field, meta, (event) => {
            field.onChange(event);
            if (onChange) onChange(event);
          })
        }
      </FastField>
    );
  }

  const { setValue } = helpers;

  return renderTextField(field, meta, (event) => {
    setValue(event.target.value, validateOnChange);
    if (onChange) onChange(event);
  });
};
